import React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useTranslation } from 'react-i18next';
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import ChatWidget from "./ChatWidget";



function LayoutApp({ signOut, user }) {
  const { t } = useTranslation();
  
  const userName = user?.signInDetails?.loginId || user?.username || t('user.guest');

  const defaultTheme = createTheme({
    palette: {
      primary: {
        main: "#0000ff", // Blue
        light: "#4d4dff", // Light blue
        dark: "#0000cc", // Dark blue
      },
      secondary: {
        main: "#000000", // Black
        light: "#333333", // Light black
        dark: "#000000", // Black
      },
      background: {
        default: "#f9f9f9", // Light neutral background
        paper: "#ffffff",
      },
      text: {
        primary: "#000000", // Black text
        secondary: "#666666", // Gray text
      },
    },
  });



  return (
    <ThemeProvider theme={defaultTheme}>
      <GlobalStyles
        styles={{ ul: { margin: 0, padding: 0, listStyle: "none" } }}
      />
      <CssBaseline />
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          background: "#000000", // Black
          position: "relative",
        }}
      >
        <Toolbar sx={{ 
          flexWrap: "wrap", 
          p: 1.5, 
          m: 0,
        }}>
          <Typography
            variant="h6"
            noWrap
            sx={{ 
              flexGrow: 1, 
              p: 0, 
              m: 0,
              color: '#ffffff', // White text
              fontWeight: 600,
            }}
          >
            {t('app.title')}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
          }}>
            <Box sx={{ display: { xs: "none", sm: "inline" } }}>
              <Chip
                sx={{
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontSize: "0.95em",
                  color: '#ffffff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  "& .MuiChip-icon": {
                    color: '#ffffff',
                  },
                }}
                label={userName}
                variant="outlined"
                icon={<SentimentSatisfiedAltIcon />}
              />
            </Box>
            {signOut && (
              <Button
                variant="outlined"
                size="small"
                onClick={signOut}
                sx={{ 
                  ml: 1,
                  color: '#ffffff',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: '#ffffff',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                {t('auth.signOut')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Container disableGutters maxWidth="xl" component="main" sx={{ minHeight: 'calc(100vh - 200px)' }}>
        {/* Main content area - can add landing page content here */}
      </Container>
      
      {/* Floating Chat Widget */}
      <ChatWidget userName={userName} />
      
      <Box textAlign={"center"}>
        <Typography
          variant="body2"
          sx={{ pb: 1, pl: 2, pr: 2, fontSize: "0.775rem" }}
        >
          &copy;{new Date().getFullYear()}, {t('app.copyright')}
        </Typography>
      </Box>
    </ThemeProvider>
  );
}

export default LayoutApp;
